/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Checkbox, Loader, TableWrapper, TableSizeControls } from 'ui-kit';
import { API_URLS, BUTTON_TYPES } from '_constants';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRetailers } from '_entities/old/Retailers/retailers.service';
import Filter from 'ui-kit/old/filter';
import { isEmpty, reduce } from 'lodash';
import { isValidDate } from 'utils/old/checkSearchDate';
import HeaderCell from 'modules/old/cells/HeaderCell';
import TextCell from 'modules/old/cells/TextCell';
import DEFAULT_SORT from '_constants/old/_constants/DEFAULT_SORT';
import { useWindowDimensions } from 'hooks';
import { addExistingRetailersToCampaign } from '_entities/Campaign/Campaign.actions';
import apiData from '_entities/old/api/config';
import axios from 'axios';
import { notification } from 'utils/services';
import * as Styled from '../styles';
import * as Styles from './styles';

const { baseUrl } = apiData;

const addExistingRetailersToTheCampaignRequest = async (data, campaignId) => {
  const token = localStorage.getItem('Authorization');
  return axios.post(`${baseUrl}${API_URLS.campaigns}/${campaignId}/retailers/add`, data, {
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
};

function AddExistingRetailerModal({ isOpen, onClose, campaignId, loadAssignments }) {
  const { width: viewportWidth } = useWindowDimensions();
  const [currentPerPage, setCurrentPerPage] = useState(20);
  const [selectedRetailersIds, setSelectedRetailersIds] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAttachDisabled, setIsAttachDisabled] = useState(false);

  const dispatch = useDispatch();
  const [sortOption, setSortOption] = useState({
    sortBy: DEFAULT_SORT.retailers.prop,
    sortOrder: DEFAULT_SORT.retailers.order,
  });
  const {
    oldRetailers: {
      retailersTableData: { pagination, data },
    },
    oldFilter: { generalFilters, shadowFilters },
  } = useSelector((state) => state);

  if (!isOpen) return null;

  const handleProceedSubmition = async () => {
    const retailersToAdd = reduce(
      selectedRetailersIds,
      (acc, value, key) => {
        if (!value) return acc;
        return [...acc, { type: 'retailers', id: key }];
      },
      [],
    );
    if (isEmpty(retailersToAdd)) {
      notification.error('Select retailer(s) you want to add to the campaign');
      return;
    }
    try {
      setIsLoading(true);
      await addExistingRetailersToTheCampaignRequest({ data: retailersToAdd }, campaignId);
      await loadAssignments({ page: 1 });
      notification.success('Retailer(s) were successfully added to the campaign');
    } catch (err) {
      const errorMessageArray = err?.response?.data?.errors;

      if (Array.isArray(errorMessageArray)) {
        notification.error(errorMessageArray[0]?.detail || 'Something went wrong during the request');
        return;
      }
      notification.error('Something went wrong during the request');
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const handleOnChangeInputSearch = (searchVal) => {
    if (isEmpty(searchVal)) {
      setSearchValue('');
    }
    setSearchValue(searchVal);
  };

  const handleClickInput = () => {
    if (isEmpty(searchValue)) return;
    const searchString = isValidDate(searchValue);
    setCurrentPage(1);

    loadRetailers({ page: 1, search: searchString.trim() });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      const searchString = isValidDate(searchValue);
      setCurrentPage(1);
      loadRetailers({ page: 1, search: searchString.trim() });
    }
  };

  const handleRemoveSearch = () => {
    if (isEmpty(searchValue)) return;
    loadRetailers({ page: 1, search: '' });
    setSearchValue('');
  };
  const handleHeaderCheckboxChange = (event, original) => {
    setSelectedRetailersIds((prev) => {
      return { ...prev, [event.target.name]: event.target.value };
    });
  };

  const loadRetailers = useCallback(
    async ({
      page = currentPage,
      perPage = currentPerPage,
      sortBy = sortOption.sortBy,
      sortOrder = sortOption.sortOrder,
      ...rest
    } = {}) => {
      try {
        setIsLoading(true);
        await fetchRetailers(
          {
            ...rest,
            page,
            perPage,
            sortBy,
            sortOrder,
            is_current: ['t'],
            include: 'aggregationpolygon_set',
            without_campaign_id: campaignId,
          },
          dispatch,
        );
      } catch (e) {
        console.log('e', e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage, dispatch, currentPerPage, sortOption, setCurrentPage, setCurrentPerPage],
  );

  useEffect(() => {
    loadRetailers({ page: 1 });
  }, []);

  const mobileColumns = useMemo(
    () => [
      {
        Header: () => null,
        accessor: 'selected',
        width: 30,
        Cell: ({ original }) => (
          <Checkbox
            value={original.selected}
            onChange={(e) => handleHeaderCheckboxChange(e, original)}
            name={String(original.id)}
          />
        ),
      },
      {
        Header: () => <HeaderCell col="Name" sortBy="name" isHideSorting />,
        accessor: 'name',
        width: 80,
        Cell: ({ value }) => <TextCell text={value} />,
      },
      {
        Header: () => <HeaderCell col="Address" sortBy="address" isHideSorting />,
        accessor: 'address',
        width: 80,
        Cell: ({ original: { address } }) => <TextCell text={`${address}`} />,
      },
      {
        Header: () => <HeaderCell col="City" sortBy="city" isHideSorting />,
        accessor: 'city',
        width: 80,
        Cell: ({ original: { city } }) => <TextCell text={`${city}`} />,
      },
      {
        Header: () => <HeaderCell col="Zipcode" sortBy="zipcode" isHideSorting />,
        accessor: 'zipcode',
        width: 70,
        Cell: ({ original: { zipcode } }) => <TextCell text={`${zipcode}`} />,
      },
      {
        Header: () => <HeaderCell col="County" sortBy="county_name" isHideSorting />,
        accessor: 'county_name',
        width: 80,
        Cell: ({ original }) => <TextCell text={`${original?.county_name}`} />,
      },
    ],
    [data, handleHeaderCheckboxChange],
  );

  const columns = useMemo(
    () => [
      {
        Header: () => null,
        accessor: 'selected',
        width: 60,
        Cell: ({ original }) => (
          <Checkbox
            value={original.selected}
            onChange={(e) => handleHeaderCheckboxChange(e, original)}
            name={String(original.id)}
          />
        ),
      },
      {
        Header: () => <HeaderCell col="Name" sortBy="name" isHideSorting />,
        accessor: 'name',
        width: 120,
        Cell: ({ value }) => <TextCell text={value} />,
      },
      {
        Header: () => <HeaderCell col="Address" sortBy="address" isHideSorting />,
        accessor: 'address',
        width: 120,
        Cell: ({ original: { address } }) => <TextCell text={`${address}`} />,
      },
      {
        Header: () => <HeaderCell col="City" sortBy="city" isHideSorting />,
        accessor: 'city',
        width: 120,
        Cell: ({ original: { city } }) => <TextCell text={`${city}`} />,
      },
      {
        Header: () => <HeaderCell col="Zipcode" sortBy="zipcode" isHideSorting />,
        accessor: 'zipcode',
        width: 120,
        Cell: ({ original: { zipcode } }) => <TextCell text={`${zipcode}`} />,
      },
      {
        Header: () => <HeaderCell col="County" sortBy="county_name" isHideSorting />,
        accessor: 'county_name',
        width: 120,
        Cell: ({ original }) => <TextCell text={`${original?.county_name}`} />,
      },
    ],
    [data, handleHeaderCheckboxChange],
  );

  const handlePerPageChange = useCallback(
    ({ value }) => {
      if (currentPerPage === value) return;
      setCurrentPerPage(value);
      setCurrentPage(1);
      loadRetailers({ perPage: value, page: 1, search: searchValue });
    },
    [setCurrentPerPage, setCurrentPage, loadRetailers, searchValue],
  );

  const handlePageChange = useCallback(
    ({ value }) => {
      if (currentPage === value) return;
      setCurrentPage(value);
      console.log('searchValue', searchValue);
      loadRetailers({ page: value, search: searchValue });
    },
    [loadRetailers, setCurrentPage, searchValue],
  );

  if (isLoading) return <Loader />;

  return (
    <Modal open={isOpen} onClose={onClose} preventEscape preventDesktopOverflow className="add_retailers_modal">
      <Styles.ModalWrapper>
        <Styled.ModalTitle>Add existing retailer</Styled.ModalTitle>
        <Styled.ModalDescription>Select the retailer you'd like to add to this campaign</Styled.ModalDescription>
        <Styles.SelectWrapper>
          <Filter
            type="null"
            placeholder="Search by retailer name, address, city and/or zip code"
            onChangeInput={handleOnChangeInputSearch}
            handleClickInput={handleClickInput}
            onKeyDown={handleKeyDown}
            //   onKeyDownFilter={onKeyDownFilter}
            onRemoveSearch={handleRemoveSearch}
            searchValue={searchValue}
            isWidth
          />

          <Styled.ModalButtonSet
            onClick={handleProceedSubmition}
            text="Add"
            variant={BUTTON_TYPES.DANGER}
            aria-label="Add"
            disabled={isAttachDisabled}
            className="Add-button"
          />
        </Styles.SelectWrapper>
        <TableWrapper
          columns={viewportWidth > 769 ? columns : mobileColumns}
          data={data}
          isEdit={false}
          tableControlParams={{}}
          isMapAssignments
          className="Add_existing_retailers_table"
          // getTrPropsFunctions={getTrPropsFunctions}
        />
        <TableSizeControls
          itemsPerPageProps={{
            handlePerPageChange,
            value: currentPerPage,
          }}
          pageCountProps={{
            value: currentPage,
            pages: pagination?.pages || 1,
            handlePageChange,
          }}
          paginationInfoProps={{
            total: pagination?.count || 0,
            currentPage,
            perPage: currentPerPage,
          }}
          paginationNavProps={{
            currentPage,
            handlePageChange,
            pages: pagination?.pages || 1,
            titleTable: 'Retailers',
          }}
        />
      </Styles.ModalWrapper>
    </Modal>
  );
}

AddExistingRetailerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loadAssignments: PropTypes.func,
  campaignId: PropTypes.string,
};

AddExistingRetailerModal.defaultProps = {
  campaignId: '',
  loadAssignments: () => {},
};

export default AddExistingRetailerModal;
